import { useCallback, useEffect, useRef, useState } from 'react';
import {
  _mergeLlamada,
  _reconciliarFeed,
  fetchLlamadasDelDia,
  suscribirLlamadas,
} from '../services/llamadas';
import type { LlamadaFeedRow } from '../types/llamadas';

// Feed del día: fetch inicial + merge de eventos realtime por id (orden
// inicio DESC lo mantiene _mergeLlamada; eventos de otros días se descartan).
// `recargar` relanza el fetch — también ante reconexión del canal, para no
// perder eventos emitidos durante un corte del websocket.
export function useLlamadas(dia: string): {
  llamadas: LlamadaFeedRow[];
  cargando: boolean;
  error: string | null;
  recargar: () => void;
} {
  const [llamadas, setLlamadas] = useState<LlamadaFeedRow[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generacion, setGeneracion] = useState(0);

  // El canal se suscribe una sola vez; el día visible viaja por ref para que
  // el merge use siempre el actual sin re-suscribir.
  const diaRef = useRef(dia);
  diaRef.current = dia;

  const recargar = useCallback(() => setGeneracion((g) => g + 1), []);

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    setError(null);
    fetchLlamadasDelDia(dia)
      .then((filas) => {
        if (!vigente) return;
        // no pisar eventos realtime llegados mientras el fetch estaba en vuelo
        setLlamadas((previas) => _reconciliarFeed(filas, previas, dia));
        setCargando(false);
      })
      .catch((e: unknown) => {
        if (!vigente) return;
        setError(e instanceof Error ? e.message : String(e));
        setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, [dia, generacion]);

  useEffect(() => {
    // cleanup idempotente (StrictMode monta el effect dos veces en dev)
    const desuscribir = suscribirLlamadas((llamada) => {
      let identificadoNuevo = false;
      setLlamadas((prev) => {
        const previa = prev.find((x) => x.id === llamada.id);
        // el payload realtime no trae el embed: conservar la razón social
        // previa si el vínculo no cambió
        const cliente = previa && previa.id_cliente === llamada.id_cliente ? previa.cliente : null;
        identificadoNuevo =
          llamada.id_cliente !== null && (!previa || previa.id_cliente !== llamada.id_cliente);
        return _mergeLlamada(prev, { ...llamada, cliente }, diaRef.current);
      });
      // el bot identificó al cliente en mitad de la llamada: refetch para
      // resolver su razón social en el feed
      if (identificadoNuevo) recargar();
    }, recargar);
    return desuscribir;
  }, [recargar]);

  return { llamadas, cargando, error, recargar };
}
